import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  Output,
  Signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { type ThemePalette } from '@angular/material/core';
import { Event, NavigationEnd, Router, Scroll } from '@angular/router';
import { ChatListDialogComponent } from '@principle-theorem/ng-chats';
import {
  IntercomService,
  resolveHelpArticleId,
} from '@principle-theorem/ng-intercom';
import { MarketingCampaignService } from '@principle-theorem/ng-marketing';
import { IToolbarAlert } from '@principle-theorem/ng-principle-shared';
import {
  BasicDialogService,
  BreakpointService,
  DialogPresets,
  SidebarManagerService,
} from '@principle-theorem/ng-shared';
import { MARKETING_CAMPAIGNS_FEATURE } from '@principle-theorem/principle-core/features';
import { first } from 'lodash';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { SearchComponent } from './search/search.component';
import { SearchService } from './search/search.service';

@Component({
  selector: 'pr-toolbar',
  templateUrl: './toolbar.component.html',
  styleUrls: ['./toolbar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ToolbarComponent {
  private _marketingService = inject(MarketingCampaignService);
  toolbarAlert: Signal<IToolbarAlert | undefined>;
  helpArticleId$: Observable<number | undefined>;
  marketingCampaignsFeature = MARKETING_CAMPAIGNS_FEATURE;
  // eslint-disable-next-line @angular-eslint/no-input-rename
  @Input('matBadge') color: ThemePalette;
  @Input() chatCount = 0;
  @Input() notificationsCount = 0;
  @Output() notificationsToggle: EventEmitter<void> = new EventEmitter<void>();

  constructor(
    private _router: Router,
    private _dialog: BasicDialogService,
    public intercom: IntercomService,
    public sidebar: SidebarManagerService,
    public searchService: SearchService,
    public breakpoint: BreakpointService
  ) {
    this.helpArticleId$ = this._router.events.pipe(
      filter(this._isScrollEvent),
      map(this._getRouteSegments),
      map(resolveHelpArticleId)
    );
    this.toolbarAlert = toSignal(
      this._marketingService
        .getToolbarAlerts$()
        .pipe(map((alerts) => first(alerts))),
      { initialValue: undefined }
    );
  }

  async logout(): Promise<void> {
    await this._router.navigate(['/login']);
  }

  search(): void {
    this._dialog.open(SearchComponent, DialogPresets.large({ height: '90vh' }));
  }

  async showChats(): Promise<void> {
    await this._dialog
      .open<ChatListDialogComponent>(
        ChatListDialogComponent,
        DialogPresets.flex({
          position: {
            bottom: '20px',
            right: '20px',
            top: '20px',
          },
          width: '400px',
          backdropClass: 'no-backdrop',
          autoFocus: true,
        })
      )
      .afterClosed()
      .toPromise();
  }

  private _isScrollEvent(event: Event): event is Scroll {
    return event instanceof Scroll;
  }

  private _getRouteSegments(event: Scroll): string[] {
    const routerEvent = event.routerEvent;
    const url =
      routerEvent instanceof NavigationEnd
        ? routerEvent.urlAfterRedirects
        : routerEvent.url;
    return url.split('/');
  }
}
